import React, { Suspense, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Home,
  User,
  Brain,
  Dumbbell,
  Activity,
  MessageCircle,
  Settings,
  UserPlus,
  TrendingUp,
  ClipboardCheck,
  Menu,
  ChefHat,
  Pill,
  FileText,
  HelpCircle,
  CalendarDays,
  Video,
} from 'lucide-react';
import { messagesStore } from '@/data/storage/messages/adapter';
import { readAndClearChatNav } from '../utils/nav';
import { SignUpFlow } from '../components/SignUpFlow';
import { ClientChat } from '../components/Client/Chat/ClientChat';
import { WorkoutDetailScreen } from '../components/Client/WorkoutDetailScreen';
import { UpdateProgressScreen } from '../components/Client/Goals/UpdateProgressScreen';
import ClientNutritionView from '../components/ClientNutritionView';
import { DashboardErrorBoundary } from '../components/ErrorBoundary';
import ClientNutritionPrint from '../components/ClientNutritionPrint';
import ProgramWeekPrint from '../components/print/ProgramWeekPrint';
import WorkoutLibrary from '../components/WorkoutLibrary';
import { ClientSupplementsView } from '../components/ClientSupplementsView';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { ToastProvider } from '../components/ui/useToastLite';
import AuthCallback from '@/pages/AuthCallback';
import ResetPassword from '@/pages/ResetPassword';
import Join from '@/pages/Join';
import Signup from '@/pages/Signup';
import IntakeReview from '@/pages/IntakeReview';
import Onboarding from '@/pages/Onboarding';
import CoachAuth from '@/pages/CoachAuth';
import CoachOnboarding from '@/pages/CoachOnboarding';
import ClientSignIn from '@/pages/ClientSignIn';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useGlobalState } from '../components/GlobalStateProvider';
import { parseClientDeepLink } from '../utils/deeplink';
import type { NavigateFn } from '../types/navigation';
import { logger } from '../utils/logger';
import { Sidebar } from '../components/Sidebar';
import { getCoachNavigation, getClientNavigation } from './utils/navigationConfig';
import { AgentBubble } from '../components/Agent/AgentBubble';
import { JarvisOverlay } from '../components/Agent/JarvisOverlay';
import type { JarvisPromptSeed, JarvisRailAppContext, JarvisRailContext } from '../components/Agent/jarvisTypes';
import { getSupabase } from '@/lib/supabase';
import { listClientEvents } from '@/data/clientEvents';
import { countUnreadSupplementChanges, countUnreadFormAssignments } from './utils/supplementNotifications';
import { resolvePath, isSpecialRoute, pushRoute, replaceRoute, getDocumentTitle } from './utils/routeSlugs';
import { useSubscription } from './hooks/useSubscription';
import type { ChatScope } from './types/agent';
import { reportSwallowed } from '@/lib/sentry';

// ── Lazy-loaded page/dashboard components (code-split) ──
const ClientDashboard = React.lazy(() =>
  import('../components/ClientDashboard').then((m) => ({ default: m.ClientDashboard })),
);
const CoachDashboardV2 = React.lazy(() =>
  import('../components/CoachDashboardV2').then((m) => ({ default: m.CoachDashboardV2 })),
);
const ClientManagement = React.lazy(() =>
  import('../components/ClientManagement').then((m) => ({ default: m.ClientManagement })),
);
const ClientWorkouts = React.lazy(() =>
  import('../components/ClientWorkouts').then((m) => ({ default: m.ClientWorkouts })),
);
const ProgramBuilder = React.lazy(() =>
  import('../components/ProgramBuilder').then((m) => ({ default: m.ProgramBuilder })),
);
const NutritionDashboard = React.lazy(() => import('../components/NutritionDashboard'));
const Chat = React.lazy(() => import('../components/Chat').then((m) => ({ default: m.Chat })));
const AIToolsDashboard = React.lazy(() =>
  import('../components/AIToolsDashboard').then((m) => ({ default: m.AIToolsDashboard })),
);
const WearableIntegration = React.lazy(() =>
  import('../components/WearableIntegration').then((m) => ({ default: m.WearableIntegration })),
);
const FormsTrackersHub = React.lazy(() =>
  import('../components/FormsTrackers/FormsTrackersHub').then((m) => ({ default: m.FormsTrackersHub })),
);
const SupplementsLibrary = React.lazy(() =>
  import('../components/SupplementsLibrary').then((m) => ({ default: m.SupplementsLibrary })),
);
const CoachCalendar = React.lazy(() =>
  import('../components/Calendar/CoachCalendar').then((m) => ({ default: m.CoachCalendar })),
);
const AutomationsDashboard = React.lazy(() =>
  import('../components/AutomationsDashboard').then((m) => ({ default: m.AutomationsDashboard })),
);
const CoachSettings = React.lazy(() =>
  import('../components/CoachSettings').then((m) => ({ default: m.CoachSettings })),
);
const CoachUpgradeHub = React.lazy(() =>
  import('../components/CoachUpgradeHub').then((m) => ({ default: m.CoachUpgradeHub })),
);
const ClientBillingPage = React.lazy(() =>
  import('../components/ClientBillingPage').then((m) => ({ default: m.ClientBillingPage })),
);
const PaymentsPage = React.lazy(() =>
  import('../components/Client/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const ClientSettings = React.lazy(() =>
  import('../components/ClientSettings').then((m) => ({ default: m.ClientSettings })),
);
const ClientGoals = React.lazy(() => import('../components/ClientGoals').then((m) => ({ default: m.ClientGoals })));
const CoachVideosLibrary = React.lazy(() =>
  import('../components/Client/CoachVideosLibrary').then((m) => ({ default: m.CoachVideosLibrary })),
);
const ClientProgress = React.lazy(() =>
  import('../components/ClientProgress').then((m) => ({ default: m.ClientProgress })),
);
const AddClientWorkflow = React.lazy(() =>
  import('../components/AddClientWorkflow').then((m) => ({ default: m.AddClientWorkflow })),
);
const AgentPanel = React.lazy(() => import('../components/Agent/AgentPanel').then((m) => ({ default: m.AgentPanel })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

/**
 * Landing page rendered by Stripe Checkout's `success_url` / `cancel_url`.
 * Auto-redirects the client back to /payments after a short countdown so they
 * end up in the right place instead of staring at a terminal "close this page"
 * screen. A manual button is provided in case the timer fails (blocked by the
 * browser, network hiccup, etc.).
 */
function CheckoutResultPage({ isSuccess }: { isSuccess: boolean }) {
  const [secondsLeft, setSecondsLeft] = useState(5);

  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const redirect = setTimeout(() => {
      window.location.replace('/payments');
    }, 5000);
    return () => {
      clearInterval(tick);
      clearTimeout(redirect);
    };
  }, []);

  return (
    <ToastProvider>
      <div className="dark min-h-screen bg-background flex items-center justify-center">
        <div className="mx-auto max-w-md text-center space-y-4 p-8">
          {isSuccess ? (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15">
                <svg
                  className="h-8 w-8 text-emerald-600 dark:text-emerald-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-foreground">Payment Successful</h1>
              <p className="text-muted-foreground">
                Thank you! Your payment has been processed. Returning you to your payments
                {secondsLeft > 0 ? ` in ${secondsLeft}s` : ' now'}…
              </p>
            </>
          ) : (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <svg
                  className="h-8 w-8 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-foreground">Payment Canceled</h1>
              <p className="text-muted-foreground">
                The payment was canceled. No charges were made. Returning you to your payments
                {secondsLeft > 0 ? ` in ${secondsLeft}s` : ' now'}…
              </p>
            </>
          )}
          <Button onClick={() => window.location.replace('/payments')} className="mt-2">
            Back to Payments
          </Button>
        </div>
      </div>
    </ToastProvider>
  );
}

type UserType = 'client' | 'coach' | null;

type TabItem = {
  id: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  badge?: number;
};

export default function App() {
  const gs = useGlobalState() as any;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState<UserType>(null);

  // Hydration-safe initial tab + sub-tab from URL (resolves aliases, normalizes, never throws)
  const [initialResolved] = useState(() => {
    if (typeof window === 'undefined') return { tab: 'dashboard', subTab: '' };
    if (isSpecialRoute(window.location.pathname)) return { tab: 'dashboard', subTab: '' };
    const r = resolvePath(window.location.pathname, window.location.search);
    return { tab: r.tab, subTab: r.subTab };
  });
  const [activeTab, setActiveTab] = useState(initialResolved.tab);
  const [activeSubTab, setActiveSubTab] = useState<string>(initialResolved.subTab);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedChatClient, setSelectedChatClient] = useState<string>('');
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [sidebarVisuallyExpanded, setSidebarVisuallyExpanded] = useState(true);
  const [clientFilter, setClientFilter] = useState<'all' | 'connected' | 'pending' | 'archived'>(() => {
    const subToFilter: Record<string, 'all' | 'connected' | 'pending' | 'archived'> = {
      'clients-all': 'all',
      'clients-connected': 'connected',
      'clients-pending': 'pending',
      'clients-archived': 'archived',
    };
    return subToFilter[initialResolved.subTab] || 'all';
  });
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);

  // AI Agent state
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [jarvisRailContext, setJarvisRailContext] = useState<JarvisRailContext | null>(null);
  const [jarvisPromptSeed, setJarvisPromptSeed] = useState<JarvisPromptSeed | null>(null);

  const effectiveUserType: 'client' | 'coach' = (() => {
    const gsUserType = gs?.userType as any;
    const localUserType = userType as any;
    const storageUserType = (() => {
      try {
        const v = localStorage.getItem('coachd:userType');
        return v === 'coach' || v === 'client' ? v : null;
      } catch {
        return null;
      }
    })();

    const result = localUserType || storageUserType || gsUserType || 'client';

    return result;
  })();

  // Billing: always call hook (hooks must be unconditional); gate logic uses effectiveUserType in JSX.
  // `/api/billing/status` is coach-only and 403s for clients, so gate the
  // actual fetch on role to keep the console clean in client mode.
  useSubscription(effectiveUserType === 'coach');

  // Mobile screen navigation states
  const [workoutDetailOpen, setWorkoutDetailOpen] = useState(false);
  const [updateProgressOpen, setUpdateProgressOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);

  // Unread counters from messaging selectors (fallback to 0)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadCheckinReplies, setUnreadCheckinReplies] = useState(0);
  const [unreadSupplementChanges, setUnreadSupplementChanges] = useState(0);
  const [unreadFormAssignments, setUnreadFormAssignments] = useState(0);
  const [clientHasPayments, setClientHasPayments] = useState(false);
  const [clientPendingPaymentsCount, setClientPendingPaymentsCount] = useState(0);
  const [chatFocus, setChatFocus] = useState<ReturnType<typeof readAndClearChatNav> | null>(null);

  // Load pending invites for coach
  const reloadPendingInvites = React.useCallback(async () => {
    if (effectiveUserType !== 'coach' || !gs?.currentCoach?.id) return;
    try {
      const { invitesStore } = await import('@/data/storage/app/invites.adapter');
      const coachInvites = await invitesStore.listByCoach(gs.currentCoach.id);
      const pending = (coachInvites || []).filter((inv: any) => inv.status === 'pending');
      setPendingInvites(pending);
    } catch (e) {
      console.error('Failed to load pending invites:', e);
      setPendingInvites([]);
    }
  }, [effectiveUserType, gs?.currentCoach?.id]);

  React.useEffect(() => {
    void reloadPendingInvites();
  }, [reloadPendingInvites, activeTab]);

  useEffect(() => {
    if (effectiveUserType !== 'client' || !gs?.authUser?.id) {
      setClientHasPayments(false);
      setClientPendingPaymentsCount(0);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch('/api/clients/payments', { credentials: 'include' });
        if (!res.ok) return;

        const json = await res.json();
        if (cancelled || !json.ok || !Array.isArray(json.payments)) return;

        const pendingCount = json.payments.filter((payment: any) => payment.status === 'pending').length;
        setClientHasPayments(json.payments.length > 0);
        setClientPendingPaymentsCount(pendingCount);
      } catch {
        if (!cancelled) {
          setClientHasPayments(false);
          setClientPendingPaymentsCount(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveUserType, gs?.authUser?.id, activeTab]);

  // Load AI Agent feature flag.
  // Default is ON for every coach: the bubble should always be discoverable, and
  // actual chat usage is gated server-side by requireProAI (free tier sees the
  // upgrade CTA inside AgentPanel). The feature_flags row is kept as a per-coach
  // kill switch — only an explicit `agent_enabled === false` hides the bubble.
  useEffect(() => {
    const checkAgentFlag = async () => {
      if (effectiveUserType !== 'coach' || !gs?.currentCoach?.id) {
        setAgentEnabled(false);
        return;
      }

      const supabase = getSupabase();
      if (!supabase) {
        setAgentEnabled(true);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('feature_flags')
          .select('agent_enabled')
          .eq('coach_id', gs.currentCoach.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('[App] Failed to load feature flags:', error);
        }

        setAgentEnabled(data?.agent_enabled !== false);
      } catch (error) {
        console.error('[App] Feature flag check failed:', error);
        setAgentEnabled(true);
      }
    };

    checkAgentFlag();
  }, [effectiveUserType, gs?.currentCoach?.id]);

  useEffect(() => {
    let mounted = true;
    const compute = async () => {
      try {
        if (effectiveUserType === 'coach') {
          const coachId = String(gs?.currentCoach?.id ?? 'coach_demo');
          if (coachId === 'coach_demo') {
            if (mounted) setUnreadMessageCount(0);
            return;
          }
          const n: any = await messagesStore.getUnreadCounts({ coachId });
          const total = typeof n === 'number' ? n : (n?.total ?? 0);
          if (mounted) setUnreadMessageCount(total);
        } else if (effectiveUserType === 'client') {
          const cid = String(gs?.authUser?.id ?? (gs?.clients || [])[0]?.id ?? 'c1');
          if (cid === 'c1') {
            if (mounted) setUnreadMessageCount(0);
            return;
          }
          const n: any = await messagesStore.getUnreadCounts({ clientId: cid });
          const total = typeof n === 'number' ? n : (n?.total ?? 0);
          if (mounted) setUnreadMessageCount(total);
        } else {
          if (mounted) setUnreadMessageCount(0);
        }
      } catch (error) {
        console.error('[App] Failed to compute unread count:', error);
        if (mounted) setUnreadMessageCount(0);
      }
    };

    // Compute on mount
    compute();

    // Poll for updates every 5 seconds
    const pollInterval = setInterval(compute, 5000);

    const onFocus = () => compute();
    window.addEventListener('focus', onFocus);
    // Cross-tab: storage + BroadcastChannel
    const onStorage = (e: StorageEvent) => {
      if (!e) return;
      if (e.key && (e.key.startsWith('coachd-message-threads') || e.key.startsWith('coachd-messages'))) compute();
    };
    window.addEventListener('storage', onStorage);
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('coachd-messages');
      bc.onmessage = () => compute();
    } catch {
      // OBS-11 a-ignore: BroadcastChannel is missing in some browsers and throws once closed; cross-tab sync is an enhancement, polling still refreshes.
    }

    return () => {
      mounted = false;
      clearInterval(pollInterval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      try {
        if (bc) bc.onmessage = null;
      } catch {
        // OBS-11 a-ignore: teardown; the channel/listener may already be closed.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserType, gs?.currentCoach?.id, gs?.authUser?.id]);

  // Poll for unread coach replies on check-ins (client only)
  useEffect(() => {
    if (effectiveUserType !== 'client') {
      setUnreadCheckinReplies(0);
      return;
    }
    let mounted = true;

    const fetchUnread = async () => {
      try {
        const res = await fetch('/api/clients/forms/coach-replies', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const replies: { sentAt: string }[] = data.replies || [];
        const lastSeen = localStorage.getItem('coachd:lastSeenRepliesAt') || '1970-01-01T00:00:00Z';
        const unread = replies.filter((r) => r.sentAt > lastSeen).length;
        if (mounted) setUnreadCheckinReplies(unread);
      } catch {
        // Silently ignore — badge just stays at 0
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 30_000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [effectiveUserType]);

  // Poll for unread supplement changes and form assignments (client only)
  useEffect(() => {
    if (effectiveUserType !== 'client') {
      setUnreadSupplementChanges(0);
      setUnreadFormAssignments(0);
      return;
    }
    let mounted = true;

    const fetchUnreadCounts = async () => {
      try {
        const events = await listClientEvents(100);
        const lastSeenSupps = localStorage.getItem('coachd:lastSeenSupplementsAt') || '1970-01-01T00:00:00.000Z';
        const unreadSupps = countUnreadSupplementChanges(events as any[], lastSeenSupps);
        const lastSeenForms = localStorage.getItem('coachd:lastSeenFormAssignmentsAt') || '1970-01-01T00:00:00.000Z';
        const unreadForms = countUnreadFormAssignments(events as any[], lastSeenForms);
        if (mounted) {
          setUnreadSupplementChanges(unreadSupps);
          setUnreadFormAssignments(unreadForms);
        }
      } catch {
        // Keep badge stale/zero on transient failure
      }
    };

    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30_000);
    const onFocus = () => fetchUnreadCounts();
    window.addEventListener('focus', onFocus);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [effectiveUserType]);

  // Mark supplement updates as seen when client opens Supplements tab
  useEffect(() => {
    if (effectiveUserType !== 'client') return;
    if (activeTab !== 'supplements') return;
    try {
      localStorage.setItem('coachd:lastSeenSupplementsAt', new Date().toISOString());
    } catch {
      // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
    }
    setUnreadSupplementChanges(0);
  }, [effectiveUserType, activeTab]);

  // Mark form assignments as seen when client opens Check-Ins tab
  useEffect(() => {
    if (effectiveUserType !== 'client') return;
    if (activeTab !== 'goals') return;
    try {
      localStorage.setItem('coachd:lastSeenFormAssignmentsAt', new Date().toISOString());
    } catch {
      // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
    }
    setUnreadFormAssignments(0);
  }, [effectiveUserType, activeTab]);

  // Listen for nav-to-chat intents (from Action Feed CTA)
  useEffect(() => {
    function onNav() {
      const payload = readAndClearChatNav();
      if (!payload) return;
      if (payload.scope?.mode === 'client') {
        setSelectedChatClient(payload.scope.clientId);
      } else if (payload.scope?.mode === 'all_clients') {
        setSelectedChatClient('');
      } else if (payload.clientId) {
        setSelectedChatClient(payload.clientId);
      }
      setActiveTab('chat');
      setChatFocus(payload);
    }
    window.addEventListener('coachd:navigate:chat', onNav);
    return () => window.removeEventListener('coachd:navigate:chat', onNav);
  }, []);

  // Listen for general navigation intents (upgrade buttons, etc.)
  useEffect(() => {
    function onNavigate(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (!detail?.tab) return;
      const tab = detail.tab as string;
      setActiveTab(tab);
      setActiveSubTab('');
      const ctx = detail.billing ? { billing: detail.billing } : undefined;
      pushRoute(tab, ctx);
    }
    window.addEventListener('coachd:navigate', onNavigate);
    return () => window.removeEventListener('coachd:navigate', onNavigate);
  }, []);

  // ── URL slug: canonicalize on mount ──
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isSpecialRoute(window.location.pathname)) return;
    const resolved = resolvePath(window.location.pathname, window.location.search);
    if (resolved.shouldReplace) {
      replaceRoute(resolved.tab, resolved.context, resolved.subTab || undefined);
    } else {
      document.title = getDocumentTitle(resolved.tab, undefined, resolved.subTab || undefined);
    }
  }, []);

  // ── URL slug: popstate (back/forward) listener with loop guard ──
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let lastPopUrl = '';
    const subToFilter: Record<string, 'all' | 'connected' | 'pending' | 'archived'> = {
      'clients-all': 'all',
      'clients-connected': 'connected',
      'clients-pending': 'pending',
      'clients-archived': 'archived',
    };
    function onPopState() {
      const url = window.location.pathname + window.location.search;
      if (url === lastPopUrl) return;
      lastPopUrl = url;
      if (isSpecialRoute(window.location.pathname)) return;
      const resolved = resolvePath(window.location.pathname, window.location.search);
      setActiveTab(resolved.tab);
      setActiveSubTab(resolved.subTab || '');
      document.title = getDocumentTitle(resolved.tab, undefined, resolved.subTab || undefined);
      if (resolved.context.clientId) {
        setDeepLinkClient({
          id: resolved.context.clientId,
          subview: (resolved.context.view as any) || 'overview',
          profileTab: (resolved.context as any).tab ?? null,
        });
      }
      if (subToFilter[resolved.subTab]) {
        setClientFilter(subToFilter[resolved.subTab]);
      }
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const mockWorkout = (import.meta as any).env?.DEV
    ? {
        title: 'HIIT Cardio Blast',
        duration: 30,
        tags: ['HIIT', 'Cardio', 'Fat Burn'],
        description: 'High-intensity interval training to boost cardiovascular fitness and burn calories.',
        exercises: [
          { id: '1', name: 'Burpees', sets: 4, reps: '30 sec', restTime: '30s', targetMuscles: ['Full Body', 'Core'] },
          {
            id: '2',
            name: 'Mountain Climbers',
            sets: 4,
            reps: '30 sec',
            restTime: '30s',
            targetMuscles: ['Core', 'Shoulders'],
          },
          { id: '3', name: 'Jump Squats', sets: 4, reps: '15', restTime: '45s', targetMuscles: ['Legs', 'Glutes'] },
          { id: '4', name: 'High Knees', sets: 4, reps: '30 sec', restTime: '30s', targetMuscles: ['Legs', 'Core'] },
          {
            id: '5',
            name: 'Push-up to T',
            sets: 3,
            reps: '10 each side',
            restTime: '60s',
            targetMuscles: ['Chest', 'Arms', 'Core'],
          },
        ],
      }
    : null;

  const [deepLinkClient, setDeepLinkClient] = useState<{
    id: string | null;
    subview: 'overview' | 'programs' | 'nutrition' | 'profile' | null;
    profileTab: string | null;
  }>(() => {
    // Cold-load restore: a refresh on the canonical /clients?clientId=…&view=…&tab=…
    // URL must reopen the same client, view, and profile tab (previously only the
    // legacy /clients/<id>/overview path was parsed, so refresh lost the selection).
    if (typeof window === 'undefined' || isSpecialRoute(window.location.pathname)) {
      return { id: null, subview: null, profileTab: null };
    }
    const r = resolvePath(window.location.pathname, window.location.search);
    return {
      id: r.context.clientId ?? null,
      subview: (r.context.view as any) ?? null,
      profileTab: (r.context as any).tab ?? null,
    };
  });
  // NOTE: No early returns before all hooks are declared. We compute content at the end.

  const clientTabs: TabItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'workouts', label: 'Workouts', icon: Dumbbell },
    { id: 'nutrition', label: 'Nutrition', icon: ChefHat },
    { id: 'supplements', label: 'Supplements', icon: Pill, badge: unreadSupplementChanges },
    // Tab id stays 'goals' for legacy routing, but this is the client Check-Ins
    // hub (due forms + coach responses) — real goals live under Progress.
    { id: 'goals', label: 'Check-Ins', icon: ClipboardCheck, badge: unreadCheckinReplies + unreadFormAssignments },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'messages', label: 'Messages', icon: MessageCircle, badge: unreadMessageCount },
    ...(clientHasPayments
      ? [{ id: 'payments', label: 'Payments', icon: FileText, badge: clientPendingPaymentsCount }]
      : []),
    { id: 'wearables', label: 'Health', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const coachTabs: TabItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'clients', label: 'Clients', icon: User },
    { id: 'chat', label: 'Chat', icon: MessageCircle, badge: unreadMessageCount },
    { id: 'calendar', label: 'Calendar', icon: CalendarDays },
    { id: 'add-client', label: 'Add Client', icon: UserPlus },
    { id: 'programs', label: 'Programs', icon: Dumbbell },
    { id: 'nutrition', label: 'Nutrition', icon: ChefHat },
    { id: 'supplements', label: 'Supplements', icon: Pill },
    { id: 'forms-trackers', label: 'Check-Ins', icon: FileText },
    { id: 'ai', label: 'AI Tools', icon: Brain },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const tabs: TabItem[] = effectiveUserType === 'client' ? clientTabs : coachTabs;

  // Core tabs for mobile bottom nav (most important 4)
  const mobileTabs: TabItem[] =
    effectiveUserType === 'client'
      ? [
          { id: 'dashboard', label: 'Home', icon: Home },
          { id: 'workouts', label: 'Workouts', icon: Dumbbell },
          { id: 'nutrition', label: 'Nutrition', icon: ChefHat },
          { id: 'more', label: 'More', icon: Menu },
        ]
      : [
          { id: 'dashboard', label: 'Home', icon: Home },
          { id: 'clients', label: 'Clients', icon: User },
          { id: 'chat', label: 'Chat', icon: MessageCircle, badge: unreadMessageCount },
          { id: 'more', label: 'More', icon: Menu },
        ];

  const handleTabChange = (tabId: string) => {
    if (tabId === 'more') {
      setMobileMenuOpen(true);
      return;
    }

    // Handle submenu items - map to parent tab
    let actualTab = tabId;

    // Map submenu items to their parent tabs
    if (tabId.startsWith('clients-')) {
      actualTab = 'clients';
      setActiveSubTab(tabId); // Track which submenu is active
      // Set filter based on submenu selection
      if (tabId === 'clients-all') setClientFilter('all');
      else if (tabId === 'clients-connected') setClientFilter('connected');
      else if (tabId === 'clients-pending') setClientFilter('pending');
      else if (tabId === 'clients-archived') setClientFilter('archived');

      // Dispatch event to notify EnhancedClientProfile to return to list
      window.dispatchEvent(new CustomEvent('navigate:client-tab', { detail: { tabId } }));
    } else if (tabId.startsWith('programs-')) {
      actualTab = 'programs';
      setActiveSubTab(tabId);
      // Map submenu to tab value
      if (tabId === 'programs-all') {
        // Will default to "programs" tab
      } else if (tabId === 'programs-workouts') {
        // Will set to "workouts" tab
      } else if (tabId === 'programs-exercises') {
        // Will set to "exercises" tab
      }
    } else if (tabId.startsWith('nutrition-')) {
      actualTab = 'nutrition';
      setActiveSubTab(tabId);
      // Map submenu to tab value
      if (tabId === 'nutrition-dashboard') {
        // Will default to dashboard view
      } else if (tabId === 'nutrition-meal-plans') {
        // Will set to "templates" tab
      } else if (tabId === 'nutrition-clients') {
        // Will set to "client-plans" tab
      } else if (tabId === 'nutrition-meals') {
        // Will set to "meal-library" tab
      }
    } else if (tabId.startsWith('ai-')) {
      actualTab = 'ai';
      setActiveSubTab(tabId);
      // Submenu -> tab value is handled by getTabValueFromSubmenu below.
    } else if (tabId.startsWith('forms-trackers-')) {
      actualTab = 'forms-trackers';
      setActiveSubTab(tabId);
      // Map submenu to tab value
      if (tabId === 'forms-trackers-forms') {
        // Will set to "forms" tab
      } else if (tabId === 'forms-trackers-trackers') {
        // Will set to "trackers" tab
      }
    } else {
      setActiveSubTab(''); // Clear submenu selection for non-submenu items
    }

    setActiveTab(actualTab);
    setMobileMenuOpen(false);
    // Pass the original tabId as subTab so the URL reflects the sub-path
    const subTabForRoute = actualTab !== tabId ? tabId : undefined;
    pushRoute(actualTab, undefined, subTabForRoute);
  };

  // Helper to get tab value from submenu ID
  const getTabValueFromSubmenu = (submenuId: string): string | null => {
    if (submenuId === 'programs-dashboard') return 'dashboard';
    if (submenuId === 'programs-all') return 'programs';
    if (submenuId === 'programs-workouts') return 'workouts';
    if (submenuId === 'programs-exercises') return 'exercises';
    if (submenuId === 'nutrition-dashboard') return 'dashboard';
    if (submenuId === 'nutrition-meal-plans') return 'templates';
    if (submenuId === 'nutrition-clients') return 'client-plans';
    if (submenuId === 'nutrition-meals') return 'meal-library';
    if (submenuId === 'ai-patterns') return 'patterns';
    if (submenuId === 'ai-chat') return 'chat';
    if (submenuId === 'forms-trackers-inbox') return 'inbox';
    if (submenuId === 'forms-trackers-forms') return 'forms';
    if (submenuId === 'forms-trackers-trackers') return 'trackers';
    return null;
  };

  const navigateToChat = (clientId?: string) => {
    if (clientId) {
      setSelectedChatClient(clientId);
    }
    setActiveTab('chat');
    pushRoute('chat');
  };

  const handleNavigate: NavigateFn = (path, clientId) => {
    if (path === 'chat' && clientId) {
      navigateToChat(clientId);
      pushRoute('chat');
      return;
    }
    if (typeof path === 'string' && path.startsWith('clients/')) {
      const allowed = /^clients\/[^/]+\/overview(?:\?.*)?$/.test(path);
      if (!allowed) {
        if (import.meta.env.VITE_USE_MOCKS !== 'true') {
          toast.error('Unknown client');
          return;
        }
      }
      const { clientId: cid, subview } = parseClientDeepLink(path);
      setDeepLinkClient({ id: cid || null, subview: (subview as any) || 'overview', profileTab: null });
      setActiveTab('clients');
      pushRoute('clients', { clientId: cid || undefined, view: subview || undefined });
      return;
    }
    if (typeof path === 'string' && path.includes('?insightId=')) {
      const [tab] = path.split('?');
      setActiveTab(tab);
      pushRoute(tab);
      return;
    }
    handleTabChange(path);
  };

  // Cold-load: deep link parse + client-param routing guard
  useEffect(() => {
    try {
      if (isSpecialRoute(window.location.pathname)) return;
      const url = new URL(window.location.href);
      const path = `${url.pathname}${url.search || ''}`;

      // Routing guard: if /dashboard has client-specific URL params, force client view
      if (url.pathname === '/dashboard' || url.pathname === '/') {
        const sp = new URLSearchParams(url.search);
        const hasClientParams = sp.has('wk') || sp.has('wm') || sp.has('focus');
        if (hasClientParams) {
          setActiveTab('dashboard');
          setUserType('client');
          if (gs?.setUserType) gs.setUserType('client');
          try {
            localStorage.setItem('coachd:userType', 'client');
          } catch {
            // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
          }
          return;
        }
      }

      // Deep link into client profile (e.g. /clients/<uuid>/overview?insightId=...)
      const parsed = parseClientDeepLink(path);
      if (parsed.clientId) {
        logger.info('DeepLinkDetected', { clientId: parsed.clientId, insightId: parsed.insightId });
        setActiveTab('clients');
        setDeepLinkClient({ id: parsed.clientId, subview: parsed.subview || 'overview', profileTab: null });
        pushRoute('clients', {
          clientId: parsed.clientId,
          view: parsed.subview || 'overview',
          insightId: parsed.insightId || undefined,
        });
      }
    } catch {
      // OBS-11 a-ignore: malformed deep-link params fall back to the default view.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile screen handlers
  const _handleOpenWorkoutDetail = () => {
    setWorkoutDetailOpen(true);
  };

  const handleStartWorkout = () => {
    setWorkoutDetailOpen(false);
    // In a real app, this would navigate to workout session
    logger.debug('Starting workout...');
  };

  const _handleOpenGoalUpdate = (goal: any) => {
    setSelectedGoal(goal);
    setUpdateProgressOpen(true);
  };

  const handleUpdateGoalProgress = (goalId: string, newValue: string, notes?: string) => {
    logger.debug('Updating goal progress:', { goalId, newValue, notes });
    // In a real app, this would update the goal in the backend
    setUpdateProgressOpen(false);
    setSelectedGoal(null);
  };

  const url = new URL(window.location.href);
  const isPrintView = url.searchParams.get('print') === 'nutrition' || url.searchParams.get('print') === 'programWeek';
  // Enable auth even when mocks are on: if Supabase is configured, we require session
  const envAny = (import.meta as any).env || {};
  const wantsMock = String(envAny.VITE_USE_MOCKS) === 'true';
  // When Supabase env exists, always gate the shell on a real session (mock mode is data-only).
  const needsAuth = isSupabaseConfigured();
  const hasValidAuthSession = Boolean(gs?.authUser?.id || (gs?.authSession as any)?.user?.id);

  // Fire a one-time "Email confirmed" toast if the user landed on the dashboard
  // directly from AuthCallback (e.g. an already-onboarded coach confirming an
  // email change, or a client). CoachOnboarding handles the param first when
  // it is the landing page, so this only fires when onboarding was skipped at
  // the router level. The param is stripped via history.replaceState so a
  // reload or bookmark never refires the toast.
  useEffect(() => {
    if (!gs?.authReady || !hasValidAuthSession) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') !== '1') return;
    toast.success('Email confirmed.');
    params.delete('confirmed');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, [gs?.authReady, hasValidAuthSession]);

  if (isPrintView) {
    return (
      <ToastProvider>
        <div className="min-h-screen bg-white text-black">
          {url.searchParams.get('print') === 'nutrition' ? (
            <ClientNutritionPrint />
          ) : (
            (() => {
              const clientId = url.searchParams.get('clientId') || '';
              const week = Number(url.searchParams.get('week') || '1');
              return (<ProgramWeekPrintHost clientId={clientId} weekNumber={week} />) as any;
            })()
          )}
        </div>
      </ToastProvider>
    );
  }

  function ProgramWeekPrintHost({ clientId, weekNumber }: { clientId: string; weekNumber: number }) {
    const gs = useGlobalState() as any;
    const stateShim = {
      programs: gs.programs ?? {},
      clientPrograms: gs.clientProgramsV2 ?? [],
      workouts: (gs as any).workouts ?? {},
    } as any;
    // Use existing imported selector via direct import at top-level
    // Re-import statically here by referencing window bundle is not needed; instead, lazily read from global import cache via dynamic import guarded in effect.
    const [rows, setRows] = React.useState<any>({ weekNumber, days: [] });
    React.useEffect(() => {
      import('./selectors/clientDashboard').then((mod) => {
        const { selectPrintWeekData } = mod as any;
        const next = selectPrintWeekData(stateShim as any, clientId, weekNumber);
        setRows(next);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, weekNumber, gs.programs, gs.clientProgramsV2, (gs as any).workouts]);
    const rel = (gs.clientProgramsV2 || []).find((cp: any) => cp.clientId === clientId);
    const program = rel ? gs.programs?.[rel.programId] : null;
    const wn = rows.weekNumber ?? weekNumber;
    const days = rows.days ?? [];
    const clientName = (gs.clients || []).find((c: any) => c.id === clientId)?.name;
    const programName = program?.name ?? undefined;
    return <ProgramWeekPrint clientName={clientName} programName={programName} weekNumber={wn} rows={days as any} />;
  }

  // Without Supabase, allow mock/demo shell (a11y/e2e) or SignUpFlow completion.
  const showMain = needsAuth ? hasValidAuthSession : wantsMock || !!isAuthenticated;

  const preferClientSignIn = (() => {
    try {
      return localStorage.getItem('coachd:userType') === 'client';
    } catch {
      return false;
    }
  })();

  const preferredAuthPath = preferClientSignIn ? '/client/auth' : '/coach/sign-in';

  let content: React.ReactNode = null;
  if (window.location.pathname.startsWith('/signup')) {
    // Guard: client signup requires an invite
    const search = new URLSearchParams(window.location.search);
    const hasInviteContext = !!(search.get('invite') || search.get('token'));
    if (!hasInviteContext) {
      window.location.replace('/coach/sign-up');
      return null;
    }
    return <Signup />;
  }
  if (window.location.pathname === '/coach/auth' || window.location.pathname.startsWith('/coach/auth/')) {
    try {
      const u = new URL(window.location.href);
      u.pathname = '/coach/sign-in';
      window.location.replace(u.toString());
    } catch {
      window.location.replace('/coach/sign-in');
    }
    return null;
  }
  if (window.location.pathname === '/coach/signup' || window.location.pathname.startsWith('/coach/signup/')) {
    try {
      const u = new URL(window.location.href);
      u.pathname = '/coach/sign-up';
      window.location.replace(u.toString());
    } catch {
      window.location.replace('/coach/sign-up');
    }
    return null;
  }
  if (window.location.pathname.startsWith('/coach/sign-in') || window.location.pathname.startsWith('/coach/sign-up')) {
    return <CoachAuth />;
  }
  if (window.location.pathname === '/auth/sign-in' || window.location.pathname.startsWith('/auth/sign-in/')) {
    try {
      const u = new URL(window.location.href);
      u.pathname = preferredAuthPath;
      window.location.replace(u.toString());
    } catch {
      window.location.replace(preferredAuthPath);
    }
    return null;
  }
  if (window.location.pathname.startsWith('/coach/onboarding')) {
    return <CoachOnboarding />;
  }
  // Canonical client login: /client/auth (legacy /client/signin redirects here)
  if (window.location.pathname === '/client/signin' || window.location.pathname.startsWith('/client/signin/')) {
    try {
      const u = new URL(window.location.href);
      u.pathname = '/client/auth';
      window.location.replace(u.toString());
    } catch {
      window.location.replace('/client/auth');
    }
    return null;
  }
  if (window.location.pathname.startsWith('/client/auth')) {
    return <ClientSignIn />;
  }
  if (window.location.pathname.startsWith('/join')) {
    return <Join />;
  }
  if (window.location.pathname.startsWith('/intake/review')) {
    return (
      <ToastProvider>
        <div className="dark min-h-screen bg-background">
          <IntakeReview />
        </div>
      </ToastProvider>
    );
  }
  if (window.location.pathname.startsWith('/onboarding')) {
    return (
      <ToastProvider>
        <div className="dark min-h-screen bg-background">
          <Onboarding />
        </div>
      </ToastProvider>
    );
  }
  if (window.location.pathname.startsWith('/checkout/')) {
    const isSuccess = window.location.pathname.includes('/success');
    return <CheckoutResultPage isSuccess={isSuccess} />;
  }
  if (needsAuth && window.location.pathname.startsWith('/auth/callback')) {
    content = (
      <ToastProvider>
        <div className="dark min-h-screen bg-background">
          <AuthCallback />
        </div>
      </ToastProvider>
    );
  } else if (window.location.pathname.startsWith('/reset-password')) {
    // Password reset is reachable regardless of needsAuth/authReady state:
    // users arrive here with a recovery session that the page itself verifies.
    content = (
      <ToastProvider>
        <div className="dark min-h-screen bg-background">
          <ResetPassword />
        </div>
      </ToastProvider>
    );
  } else if (needsAuth && !gs?.authReady) {
    content = <div className="grid place-items-center h-screen text-muted-foreground">Checking session…</div>;
  } else if (needsAuth && gs?.authReady && !hasValidAuthSession) {
    window.location.replace(preferredAuthPath);
    return null;
  } else if (!showMain) {
    content = (
      <ToastProvider>
        <div className="dark min-h-screen bg-background">
          <SignUpFlow
            onComplete={(type) => {
              setUserType(type);
              try {
                localStorage.setItem('coachd:userType', String(type));
              } catch {
                // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
              }
              setIsAuthenticated(true);
            }}
          />
        </div>
      </ToastProvider>
    );
  } else {
    const agentInitialScope: ChatScope = selectedChatClient
      ? { mode: 'client', clientId: selectedChatClient }
      : { mode: 'all_clients' };
    const selectedChatClientName =
      (gs?.clients || []).find((client: any) => client.id === selectedChatClient)?.name || null;
    const jarvisRailAppContext: JarvisRailAppContext = {
      initialScopeMode: selectedChatClient ? 'client' : 'all_clients',
      activeTab,
      selectedClientId: selectedChatClient || null,
      selectedClientName: selectedChatClientName,
      coachName: gs?.currentCoach?.name || null,
    };
    content = (
      <ToastProvider>
        <div className="min-h-screen bg-background text-foreground">
          <div aria-live="polite" aria-atomic="true" role="status" className="sr-only" />
          <div className="flex flex-col md:flex-row min-h-screen">
            {/* Desktop Sidebar - Fixed positioned, renders outside flow */}
            <div className="hidden md:block">
              <Sidebar
                items={
                  effectiveUserType === 'client'
                    ? getClientNavigation(
                        unreadMessageCount,
                        unreadCheckinReplies,
                        unreadSupplementChanges,
                        unreadFormAssignments,
                        clientPendingPaymentsCount,
                        clientHasPayments,
                      )
                    : getCoachNavigation(unreadMessageCount)
                }
                activeTab={activeSubTab || activeTab}
                onNavigate={handleTabChange}
                onVisualStateChange={setSidebarVisuallyExpanded}
                signOutRedirectTo={effectiveUserType === 'client' ? '/client/auth' : '/coach/sign-in'}
              />
            </div>

            {/* Mobile Header */}
            <div className="md:hidden border-b border-border bg-card p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-2">
                  <img
                    src="/branding/coachos-icon.png"
                    alt=""
                    width={36}
                    height={36}
                    className="h-9 w-9 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                    decoding="async"
                  />
                  <span className="text-lg font-semibold tracking-tight text-foreground">CoachOS</span>
                </div>
                <p className="text-xs text-muted-foreground capitalize">{effectiveUserType ?? 'guest'} Dashboard</p>
                {gs?.authUser?.email && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground truncate" title={gs.authUser.email}>
                    {gs.authUser.email}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {(import.meta as any).env?.DEV && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const next = effectiveUserType === 'coach' ? 'client' : 'coach';

                      try {
                        localStorage.setItem('coachd:userType', next);
                      } catch (e) {
                        console.error('Failed to save to localStorage:', e);
                      }

                      setUserType(next as any);
                      if (gs?.setUserType) {
                        gs.setUserType(next);
                      }

                      setTimeout(() => {
                        window.location.reload();
                      }, 100);
                    }}
                  >
                    {effectiveUserType === 'coach' ? 'Client' : 'Coach'}
                  </Button>
                )}
                {needsAuth && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      try {
                        await gs?.signOut?.();
                      } catch (err) {
                        // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
                        reportSwallowed('App.signOut', err);
                      }
                      window.location.href = effectiveUserType === 'client' ? '/client/auth' : '/coach/sign-in';
                    }}
                  >
                    Sign out
                  </Button>
                )}
              </div>
            </div>

            {/* Main Content - Dynamic left margin based on sidebar state */}
            <div
              className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarVisuallyExpanded ? 'md:ml-60' : 'md:ml-16'}`}
            >
              {/* AI Agent Bubble (Coach only, if enabled) */}
              {effectiveUserType === 'coach' && agentEnabled && (
                <>
                  <AgentBubble onClick={() => setAgentPanelOpen(true)} />
                  <JarvisOverlay
                    open={agentPanelOpen}
                    onOpenChange={setAgentPanelOpen}
                    railContext={jarvisRailContext}
                    railAppContext={jarvisRailAppContext}
                    onSeedPrompt={(prompt) => setJarvisPromptSeed({ id: Date.now(), text: prompt })}
                  >
                    {/* C6: scope crashes in the streaming AI panel so they
                        don't nuke the shell. Closing the overlay is the natural
                        recovery, so `onReset` is wired to do exactly that. */}
                    <DashboardErrorBoundary
                      fallbackTitle="AI assistant error"
                      fallbackDescription="The AI assistant hit an unexpected error. Close this panel and try again — your other work is still safe."
                      dashboardName="AgentPanel"
                      onReset={() => setAgentPanelOpen(false)}
                    >
                      <Suspense fallback={<PageLoader />}>
                        <AgentPanel
                          coachId={gs?.currentCoach?.id || gs?.authUser?.id || ''}
                          initialClientId={selectedChatClient || undefined}
                          initialScope={agentInitialScope}
                          activeTab={activeTab}
                          onNavigate={handleNavigate}
                          promptSeed={jarvisPromptSeed}
                          onJarvisContextChange={setJarvisRailContext}
                          hideQuickPrompts
                          variant="jarvis"
                        />
                      </Suspense>
                    </DashboardErrorBoundary>
                  </JarvisOverlay>
                </>
              )}

              <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto">
                <div className="p-3 pb-20 md:p-4 md:pb-4 max-w-6xl w-full">
                  {activeTab === 'dashboard' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Client Dashboard Error" dashboardName="ClientDashboard">
                      <Suspense fallback={<PageLoader />}>
                        <ClientDashboard onNavigate={handleNavigate} />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'dashboard' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Coach Dashboard Error" dashboardName="CoachDashboardV2">
                      <Suspense fallback={<PageLoader />}>
                        <CoachDashboardV2
                          onNavigateToClient={(clientId) => handleNavigate(`clients/${clientId}/overview`)}
                          onNavigateToThread={(threadId) => handleNavigate('chat', threadId)}
                        />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'clients' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Client Management Error" dashboardName="ClientManagement">
                      <Suspense fallback={<PageLoader />}>
                        <ClientManagement
                          onNavigate={handleNavigate}
                          initialSelectedClientId={deepLinkClient.id ?? undefined}
                          initialSelectedView={deepLinkClient.subview === 'programs' ? 'programs' : 'profile'}
                          initialProfileTab={deepLinkClient.profileTab ?? undefined}
                          clientFilter={clientFilter}
                          pendingInvites={pendingInvites}
                          onInvitesChanged={reloadPendingInvites}
                        />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'chat' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Chat Error" dashboardName="Chat">
                      <Suspense fallback={<PageLoader />}>
                        <Chat
                          selectedClientId={selectedChatClient}
                          onClientSelect={setSelectedChatClient}
                          focus={chatFocus || undefined}
                          onFocusConsumed={() => setChatFocus(null)}
                        />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'add-client' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Add Client Error" dashboardName="AddClientWorkflow">
                      <Suspense fallback={<PageLoader />}>
                        <AddClientWorkflow />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'workouts' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Client Workouts Error" dashboardName="ClientWorkouts">
                      <Suspense fallback={<PageLoader />}>
                        <ClientWorkouts />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'workouts' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary>
                      <WorkoutLibrary />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'nutrition' &&
                    effectiveUserType === 'client' &&
                    (() => {
                      // For client view, use the authenticated user's ID instead of 'c1'
                      const targetId = deepLinkClient.id || gs.authUser?.id || 'c1';
                      let client =
                        (gs.clients || []).find(
                          (c: any) => String(c.id).toLowerCase() === String(targetId).toLowerCase(),
                        ) || gs.clients[0];
                      // If still no client, try to infer from any plan
                      if (
                        !client &&
                        Array.isArray((gs as any).clientNutritionPlans) &&
                        (gs as any).clientNutritionPlans.length > 0
                      ) {
                        const firstPlan = (gs as any).clientNutritionPlans[0];
                        client = (gs.clients || []).find(
                          (c: any) => String(c.id).toLowerCase() === String(firstPlan.clientId).toLowerCase(),
                        ) as any;
                      }
                      const effectiveClient = client ?? ({ id: targetId, name: 'Client' } as any);
                      try {
                        const isDev = (import.meta as any).env?.DEV === true;
                        if (isDev)
                          console.debug('[App] NutritionTab render', {
                            clientId: effectiveClient?.id,
                            plans: (gs as any).clientNutritionPlans?.length,
                          });
                      } catch {
                        // OBS-11 a-ignore: dev-only debug logging must never break the real code path.
                      }
                      return (
                        <DashboardErrorBoundary>
                          <ClientNutritionView
                            client={effectiveClient}
                            clientId={targetId}
                            onBack={() => setActiveTab('dashboard')}
                            onNavigate={() => {}}
                          />
                        </DashboardErrorBoundary>
                      );
                    })()}
                  {activeTab === 'goals' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Client Goals Error" dashboardName="ClientGoals">
                      <Suspense fallback={<PageLoader />}>
                        <ClientGoals />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'videos' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Videos Error" dashboardName="CoachVideosLibrary">
                      <Suspense fallback={<PageLoader />}>
                        <CoachVideosLibrary />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'progress' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Client Progress Error" dashboardName="ClientProgress">
                      <Suspense fallback={<PageLoader />}>
                        <ClientProgress />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'messages' && effectiveUserType === 'client' && (
                    <div className="h-[calc(100vh-160px)]">
                      <ClientChat />
                    </div>
                  )}
                  {activeTab === 'payments' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Payments Error" dashboardName="PaymentsPage">
                      <Suspense fallback={<PageLoader />}>
                        <PaymentsPage />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'programs' &&
                    (() => {
                      const subTabValue = getTabValueFromSubmenu(activeSubTab || '');
                      // Programs KPI dashboard is hidden (see `coachUiVisibility`); default
                      // /programs and legacy dashboard URLs resolve to the library.
                      const initialTab =
                        subTabValue === 'workouts'
                          ? 'workouts'
                          : subTabValue === 'exercises'
                            ? 'exercises'
                            : 'programs';
                      return (
                        <DashboardErrorBoundary fallbackTitle="Program Builder Error" dashboardName="ProgramBuilder">
                          <Suspense fallback={<PageLoader />}>
                            <ProgramBuilder initialTab={initialTab} onNavigate={handleNavigate} />
                          </Suspense>
                        </DashboardErrorBoundary>
                      );
                    })()}
                  {activeTab === 'nutrition' &&
                    effectiveUserType === 'coach' &&
                    (() => {
                      const subTabValue = getTabValueFromSubmenu(activeSubTab || '');
                      // Nutrition KPI dashboard is hidden (see `coachUiVisibility`); default
                      // /nutrition and legacy dashboard URLs resolve to meal plans.
                      const initialTab =
                        subTabValue === 'client-plans'
                          ? 'client-plans'
                          : subTabValue === 'meal-library'
                            ? 'meal-library'
                            : 'templates';
                      return (
                        <DashboardErrorBoundary
                          fallbackTitle="Nutrition Manager Error"
                          dashboardName="NutritionDashboard"
                        >
                          <Suspense fallback={<PageLoader />}>
                            <NutritionDashboard mode="manager" onNavigate={handleNavigate} initialTab={initialTab} />
                          </Suspense>
                        </DashboardErrorBoundary>
                      );
                    })()}
                  {activeTab === 'forms-trackers' &&
                    effectiveUserType === 'coach' &&
                    (() => {
                      const subTabValue = getTabValueFromSubmenu(activeSubTab || '');
                      const initialFormsTab = (subTabValue || 'inbox') as 'inbox' | 'forms' | 'trackers';
                      return (
                        <DashboardErrorBoundary fallbackTitle="Check-Ins Error" dashboardName="FormsTrackersHub">
                          <Suspense fallback={<PageLoader />}>
                            <FormsTrackersHub
                              coachId={gs?.currentCoach?.id || gs?.authUser?.id || ''}
                              clients={gs?.clients || []}
                              initialTab={initialFormsTab}
                            />
                          </Suspense>
                        </DashboardErrorBoundary>
                      );
                    })()}
                  {activeTab === 'ai' &&
                    effectiveUserType === 'coach' &&
                    (() => {
                      const subTabValue = getTabValueFromSubmenu(activeSubTab || '');
                      // Default to patterns if no subtab selected
                      const initialAITab = (subTabValue || 'patterns') as 'patterns' | 'chat';
                      return (
                        <DashboardErrorBoundary fallbackTitle="AI Tools Error" dashboardName="AIToolsDashboard">
                          <Suspense fallback={<PageLoader />}>
                            <AIToolsDashboard onNavigate={handleNavigate} initialTab={initialAITab} />
                          </Suspense>
                        </DashboardErrorBoundary>
                      );
                    })()}
                  {activeTab === 'calendar' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Calendar Error" dashboardName="CoachCalendar">
                      <Suspense fallback={<PageLoader />}>
                        <CoachCalendar clients={gs?.clients || []} />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'supplements' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary
                      fallbackTitle="Supplements Library Error"
                      dashboardName="SupplementsLibrary"
                    >
                      <Suspense fallback={<PageLoader />}>
                        <SupplementsLibrary />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'supplements' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Supplements Error" dashboardName="ClientSupplementsView">
                      <ClientSupplementsView />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'wearables' && (
                    <DashboardErrorBoundary
                      fallbackTitle="Wearable Integration Error"
                      dashboardName="WearableIntegration"
                    >
                      <Suspense fallback={<PageLoader />}>
                        <WearableIntegration />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'automations' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Automations Error" dashboardName="AutomationsDashboard">
                      <Suspense fallback={<PageLoader />}>
                        <AutomationsDashboard />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'settings' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Coach Settings Error" dashboardName="CoachSettings">
                      <Suspense fallback={<PageLoader />}>
                        <CoachSettings />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'client-billing' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Client Billing Error" dashboardName="ClientBillingPage">
                      <Suspense fallback={<PageLoader />}>
                        <ClientBillingPage />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'upgrade' && effectiveUserType === 'coach' && (
                    <DashboardErrorBoundary fallbackTitle="Upgrade Error" dashboardName="CoachUpgradeHub">
                      <Suspense fallback={<PageLoader />}>
                        <CoachUpgradeHub />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'settings' && effectiveUserType === 'client' && (
                    <DashboardErrorBoundary fallbackTitle="Client Settings Error" dashboardName="ClientSettings">
                      <Suspense fallback={<PageLoader />}>
                        <ClientSettings />
                      </Suspense>
                    </DashboardErrorBoundary>
                  )}

                  {/* Placeholder for other tabs */}
                  {![
                    'dashboard',
                    'clients',
                    'chat',
                    'calendar',
                    'add-client',
                    'workouts',
                    'nutrition',
                    'goals',
                    'videos',
                    'progress',
                    'messages',
                    'payments',
                    'programs',
                    'forms-trackers',
                    'ai',
                    'wearables',
                    'supplements',
                    'automations',
                    'client-billing',
                    'upgrade',
                    'settings',
                  ].includes(activeTab) && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="capitalize">{activeTab}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground">
                          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} feature coming soon...
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </main>

              {/* Mobile Bottom Navigation - Simplified */}
              <div className="md:hidden border-t border-border bg-card fixed bottom-0 left-0 right-0 z-50">
                <div className="flex">
                  {mobileTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = tab.id !== 'more' && activeTab === tab.id;
                    return (
                      <Button
                        key={tab.id}
                        variant="ghost"
                        className={`flex-1 flex-col h-14 rounded-none relative ${isActive ? 'bg-secondary' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                      >
                        <Icon className="w-4 h-4 mb-1" />
                        <span className="text-xs">{tab.label}</span>
                        {typeof tab.badge === 'number' && tab.badge > 0 && (
                          <div className="absolute top-1 right-2 bg-red-500 text-white text-xs rounded-full min-w-4 h-4 flex items-center justify-center">
                            {tab.badge > 9 ? '9+' : tab.badge}
                          </div>
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Menu Sheet */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetContent side="bottom" className="h-auto max-h-[80vh]">
                  <SheetHeader className="pb-4">
                    <SheetTitle>All Features</SheetTitle>
                    <SheetDescription>Access all {userType ?? 'guest'} features and tools</SheetDescription>
                  </SheetHeader>

                  <div className="grid grid-cols-2 gap-3 pb-6">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;

                      return (
                        <Button
                          key={tab.id}
                          variant={activeTab === tab.id ? 'secondary' : 'outline'}
                          className="h-16 flex flex-col items-center justify-center relative"
                          onClick={() => {
                            handleTabChange(tab.id);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <Icon className="w-5 h-5 mb-2" />
                          <span className="text-xs">{tab.label}</span>
                          {typeof tab.badge === 'number' && tab.badge > 0 && (
                            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-4 h-4 flex items-center justify-center">
                              {tab.badge > 9 ? '9+' : tab.badge}
                            </div>
                          )}
                        </Button>
                      );
                    })}

                    {/* Additional Actions */}
                    <Button
                      variant="outline"
                      className="h-16 flex-col items-center justify-center"
                      onClick={() => {
                        window.open('https://os-coach.com/help', '_blank', 'noreferrer');
                        setMobileMenuOpen(false);
                      }}
                    >
                      <HelpCircle className="w-5 h-5 mb-2" />
                      <span className="text-xs">Help</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-16 flex-col items-center justify-center"
                      onClick={() => {
                        setIsAuthenticated(false);
                        setUserType(null);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <Settings className="w-5 h-5 mb-2" />
                      <span className="text-xs">Switch Account</span>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Mobile-only mock screens — DEV only */}
            {(import.meta as any).env?.DEV && userType === 'client' && (
              <>
                <WorkoutDetailScreen
                  isOpen={workoutDetailOpen}
                  onClose={() => setWorkoutDetailOpen(false)}
                  workout={mockWorkout!}
                  onStartWorkout={handleStartWorkout}
                />
                <UpdateProgressScreen
                  isOpen={updateProgressOpen}
                  onClose={() => setUpdateProgressOpen(false)}
                  goal={selectedGoal}
                  onUpdateProgress={handleUpdateGoalProgress}
                />
              </>
            )}
          </div>
        </div>

        {/* Debug Panel - Only show in development */}
        {(import.meta as any).env?.DEV && (
          <div className="fixed top-4 left-4 z-50">
            <Button
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-12 h-12 shadow-lg"
              size="sm"
            >
              🐛
            </Button>

            {showDebugPanel && (
              <div className="absolute top-16 left-0 bg-card border border-border rounded-lg shadow-xl p-4 w-80">
                <h3 className="font-semibold mb-3 text-foreground">Debug Panel</h3>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Current User Type:</p>
                    <p className="font-mono text-sm bg-muted p-2 rounded">{effectiveUserType}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        gs?.setUserType?.('coach');
                        setUserType('coach');
                      }}
                      size="sm"
                      variant={effectiveUserType === 'coach' ? 'default' : 'outline'}
                      className="flex-1"
                    >
                      Coach View
                    </Button>
                    <Button
                      onClick={() => {
                        gs?.setUserType?.('client');
                        setUserType('client');
                      }}
                      size="sm"
                      variant={effectiveUserType === 'client' ? 'default' : 'outline'}
                      className="flex-1"
                    >
                      Client View
                    </Button>
                  </div>

                  <Button
                    onClick={() => {
                      gs?.signOut?.();
                      setIsAuthenticated(false);
                      setUserType(null);
                    }}
                    size="sm"
                    variant="destructive"
                    className="w-full"
                  >
                    Sign Out
                  </Button>

                  <Button
                    onClick={() => {
                      window.location.href = '/client/auth';
                    }}
                    size="sm"
                    variant="secondary"
                    className="w-full"
                  >
                    Go to Client Sign-in
                  </Button>

                  <Button
                    onClick={() => {
                      throw new Error('Sentry test error – verify in Sentry dashboard');
                    }}
                    size="sm"
                    variant="outline"
                    className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                  >
                    Test Sentry (throw error)
                  </Button>

                  <div className="text-xs text-muted-foreground">
                    <p>Auth Ready: {gs?.authReady ? '✅' : '❌'}</p>
                    <p>Auth User: {gs?.authUser?.id ? '✅' : '❌'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <Toaster richColors position="top-center" closeButton />
      </ToastProvider>
    );
  }

  return content;
}
